<!-- ====================================================== -->
<!-- EDIT MODE -->
<!-- ====================================================== -->

<div
  *ngIf="editable; else previewMode"
>
  <!-- Toolbar -->
  <div
    class="mb-8 flex flex-col gap-4"
  >
    <div>
      <h2
        class="text-2xl font-semibold text-[#1E2A78]"
      >
        {{ 'PROPOSAL_REVIEW.SECTION_CONTENT' | translate }}
      </h2>

      <p
        class="mt-1 text-sm text-gray-500"
      >
        {{ 'PROPOSAL_REVIEW.SECTION_CONTENT_HINT' | translate }}
      </p>
    </div>

    <div
      class="flex flex-wrap gap-3"
    >
      <button
        type="button"
        (click)="addHeading()"
        class="section-add-btn"
      >
        {{ 'PROPOSAL_REVIEW.ADD_HEADING' | translate }}
      </button>

      <button
        type="button"
        (click)="addParagraph()"
        class="section-add-btn"
      >
        {{ 'PROPOSAL_REVIEW.ADD_PARAGRAPH' | translate }}
      </button>

      <button
        type="button"
        (click)="addBulletList()"
        class="section-add-btn"
      >
        {{ 'PROPOSAL_REVIEW.ADD_BULLET_LIST' | translate }}
      </button>

      <button
        type="button"
        (click)="addNumberedList()"
        class="section-add-btn"
      >
        {{ 'PROPOSAL_REVIEW.ADD_NUMBERED_LIST' | translate }}
      </button>

      <button
        type="button"
        (click)="addTable()"
        class="section-add-btn"
      >
        {{ 'PROPOSAL_REVIEW.ADD_TABLE' | translate }}
      </button>
    </div>
  </div>

  <!-- Messages -->
  <div
    *ngIf="errorMessage"
    class="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
  >
    {{ errorMessage }}
  </div>

  <div
    *ngIf="successMessage"
    class="mb-5 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700"
  >
    {{ successMessage }}
  </div>

  <div
    *ngIf="loading"
    class="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500"
  >
    {{ 'PROPOSAL_REVIEW.LOADING' | translate }}
  </div>

  <div
    *ngIf="saving"
    class="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700"
  >
    {{ 'PROPOSAL_REVIEW.SAVING' | translate }}
  </div>

  <!-- Empty state -->
  <div
    *ngIf="
      !loading &&
      activeBlocks.length === 0
    "
    class="rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-blue-50 p-10 text-center"
  >
    <h3
      class="text-lg font-semibold text-[#1E2A78]"
    >
      {{ 'PROPOSAL_REVIEW.NO_CONTENT_TITLE' | translate }}
    </h3>

    <p
      class="mt-2 text-gray-500"
    >
      {{ 'PROPOSAL_REVIEW.NO_CONTENT_HINT' | translate }}
    </p>
  </div>

  <!-- Blocks -->
  <div class="space-y-5" cdkDropList (cdkDropListDropped)="onBlockDrop($event)">
    <div
      *ngFor="
        let block of activeBlocks;
        let blockIndex = index;
        trackBy: trackByBlock
      "
      cdkDrag
      cdkDragLockAxis="y"
      class="section-drag-item overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
    >
      <!-- Block header -->
      <div
        class="flex cursor-pointer items-center justify-between gap-4 border-b bg-gray-50 px-5 py-4 transition hover:bg-gray-100"
        (click)="
          block.expanded =
            !block.expanded
        "
      >
        <div
          class="flex min-w-0 items-center gap-4"
        >
          <app-section-drag-handle cdkDragHandle (click)="$event.stopPropagation()"></app-section-drag-handle>

          <div
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-100 font-semibold text-[#1E2A78]"
          >
            {{ blockIndex + 1 }}
          </div>

          <div class="min-w-0">
            <div
              class="flex flex-wrap gap-2"
            >
              <span
                *ngIf="
                  block.type ===
                  'heading'
                "
                class="rounded-full bg-indigo-100 px-3 py-1 text-xs text-[#1E2A78]"
              >
                {{ 'PROPOSAL_REVIEW.TYPE.HEADING' | translate }}
              </span>

              <span
                *ngIf="
                  block.type ===
                  'paragraph'
                "
                class="rounded-full bg-blue-100 px-3 py-1 text-xs text-blue-700"
              >
                {{ 'PROPOSAL_REVIEW.TYPE.PARAGRAPH' | translate }}
              </span>

              <span
                *ngIf="
                  block.type ===
                  'bullet-list'
                "
                class="rounded-full bg-green-100 px-3 py-1 text-xs text-green-700"
              >
                {{ 'PROPOSAL_REVIEW.TYPE.BULLET_LIST' | translate }}
              </span>

              <span
                *ngIf="
                  block.type ===
                  'numbered-list'
                "
                class="rounded-full bg-orange-100 px-3 py-1 text-xs text-orange-700"
              >
                {{ 'PROPOSAL_REVIEW.TYPE.NUMBERED_LIST' | translate }}
              </span>

              <span
                *ngIf="
                  block.type ===
                  'table'
                "
                class="rounded-full bg-purple-100 px-3 py-1 text-xs text-purple-700"
              >
                {{ 'PROPOSAL_REVIEW.TYPE.TABLE' | translate }}
              </span>
            </div>

            <h3
              class="mt-1 truncate font-semibold text-gray-800"
            >
              {{
                block.title ||
                ((
                  block.type === 'heading'
                    ? 'PROPOSAL_REVIEW.UNTITLED.HEADING'
                    : block.type === 'paragraph'
                      ? 'PROPOSAL_REVIEW.UNTITLED.PARAGRAPH'
                      : block.type === 'bullet-list'
                        ? 'PROPOSAL_REVIEW.UNTITLED.BULLET_LIST'
                        : block.type === 'numbered-list'
                          ? 'PROPOSAL_REVIEW.UNTITLED.NUMBERED_LIST'
                          : 'PROPOSAL_REVIEW.UNTITLED.TABLE'
                ) | translate)
              }}
            </h3>

            <p
              *ngIf="
                block.type ===
                  'table' &&
                block.table
              "
              class="mt-1 text-xs text-gray-500"
            >
              {{
                'PROPOSAL_REVIEW.TABLE_SUMMARY' | translate: {
                  rows: block.table.rows.length,
                  columns: getLeafColumns(block).length
                }
              }}
            </p>
          </div>
        </div>

        <div
          class="flex shrink-0 items-center gap-3"
        >
          <button
            type="button"
            (click)="
              requestRemoveBlock(
                blockIndex
              );
              $event.stopPropagation()
            "
            class="section-delete-btn"
          >
            {{ 'COMMON.DELETE' | translate }}
          </button>

          <span
            class="text-gray-500"
          >
            {{
              block.expanded
                ? '▲'
                : '▼'
            }}
          </span>
        </div>
      </div>

      <!-- Block editor -->
      <div
        *ngIf="block.expanded"
        class="p-6"
      >
        <!-- Heading -->
        <div
          *ngIf="
            block.type ===
            'heading'
          "
        >
          <label
            class="mb-2 block text-sm font-medium text-gray-700"
          >
            {{ 'PROPOSAL_REVIEW.HEADING_TEXT' | translate }}
          </label>

          <input
            [(ngModel)]="
              block.title
            "
            [placeholder]="'PROPOSAL_REVIEW.HEADING_PLACEHOLDER' | translate"
            class="h-12 w-full rounded-xl border border-gray-300 px-4 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
        </div>

        <!-- Paragraph -->
        <div
          *ngIf="
            block.type ===
            'paragraph'
          "
        >
          <div class="mb-5">
            <label
              class="mb-2 block text-sm font-medium text-gray-700"
            >
              {{ 'PROPOSAL_REVIEW.OPTIONAL_TITLE' | translate }}
            </label>

            <input
              [(ngModel)]="
                block.title
              "
              [placeholder]="'PROPOSAL_REVIEW.PARAGRAPH_TITLE_PLACEHOLDER' | translate"
              class="h-12 w-full rounded-xl border border-gray-300 px-4 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <label
            class="mb-2 block text-sm font-medium text-gray-700"
          >
            {{ 'PROPOSAL_REVIEW.PARAGRAPH_CONTENT' | translate }}
          </label>

          <app-rich-text-editor
            [(ngModel)]="block.content"
            minHeight="200px"
            [placeholder]="'PROPOSAL_REVIEW.PARAGRAPH_CONTENT_PLACEHOLDER' | translate"
          ></app-rich-text-editor>
        </div>

        <!-- Bullet list -->
        <div
          *ngIf="
            block.type ===
            'bullet-list'
          "
        >
          <div class="mb-5">
            <label
              class="mb-2 block text-sm font-medium text-gray-700"
            >
              {{ 'PROPOSAL_REVIEW.OPTIONAL_TITLE' | translate }}
            </label>

            <input
              [(ngModel)]="
                block.title
              "
              [placeholder]="'PROPOSAL_REVIEW.BULLET_TITLE_PLACEHOLDER' | translate"
              class="h-12 w-full rounded-xl border border-gray-300 px-4 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <label
            class="mb-2 block text-sm font-medium text-gray-700"
          >
            {{ 'PROPOSAL_REVIEW.BULLET_ITEMS' | translate }}
          </label>

          <p
            class="mb-3 text-xs text-gray-500"
          >
            {{ 'PROPOSAL_REVIEW.ONE_ITEM_PER_LINE' | translate }}
          </p>

          <textarea
            [(ngModel)]="
              block.content
            "
            rows="8"
            [placeholder]="'PROPOSAL_REVIEW.LIST_ITEMS_PLACEHOLDER' | translate"
            class="min-h-[200px] w-full resize-y rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          ></textarea>
        </div>

        <!-- Numbered list -->
        <div
          *ngIf="
            block.type ===
            'numbered-list'
          "
        >
          <div class="mb-5">
            <label
              class="mb-2 block text-sm font-medium text-gray-700"
            >
              {{ 'PROPOSAL_REVIEW.OPTIONAL_TITLE' | translate }}
            </label>

            <input
              [(ngModel)]="
                block.title
              "
              [placeholder]="'PROPOSAL_REVIEW.NUMBERED_TITLE_PLACEHOLDER' | translate"
              class="h-12 w-full rounded-xl border border-gray-300 px-4 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <label
            class="mb-2 block text-sm font-medium text-gray-700"
          >
            {{ 'PROPOSAL_REVIEW.NUMBERED_ITEMS' | translate }}
          </label>

          <p
            class="mb-3 text-xs text-gray-500"
          >
            {{ 'PROPOSAL_REVIEW.NUMBERED_ITEMS_HINT' | translate }}
          </p>

          <textarea
            [(ngModel)]="
              block.content
            "
            rows="8"
            [placeholder]="'PROPOSAL_REVIEW.LIST_ITEMS_PLACEHOLDER' | translate"
            class="min-h-[200px] w-full resize-y rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          ></textarea>
        </div>

        <!-- Table -->
        <div
          *ngIf="
            block.type ===
              'table' &&
            block.table
          "
        >
          <div class="mb-5">
            <label
              class="mb-2 block text-sm font-medium text-gray-700"
            >
              {{ 'PROPOSAL_REVIEW.TABLE_TITLE' | translate }}
            </label>

            <input
              [(ngModel)]="
                block.title
              "
              [placeholder]="'PROPOSAL_REVIEW.TABLE_TITLE_PLACEHOLDER' | translate"
              class="h-12 w-full rounded-xl border border-gray-300 px-4 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <div class="mb-5">
            <label
              class="mb-2 block text-sm font-medium text-gray-700"
            >
              {{ 'PROPOSAL_REVIEW.TABLE_NOTE' | translate }}
            </label>

            <textarea
              [(ngModel)]="
                block.content
              "
              rows="3"
              [placeholder]="'PROPOSAL_REVIEW.TABLE_NOTE_PLACEHOLDER' | translate"
              class="w-full resize-y rounded-xl border border-gray-300 px-4 py-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            ></textarea>
          </div>

          <div
            class="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <p
              class="text-sm text-gray-500"
            >
              {{
                'PROPOSAL_REVIEW.TABLE_SUMMARY_FULL' | translate: {
                  rows: block.table.rows.length,
                  headers: block.table.columns.length,
                  columns: getLeafColumns(block).length
                }
              }}
            </p>

            <div
              class="flex flex-wrap gap-2"
            >
              <button
                type="button"
                (click)="
                  addHeader(block)
                "
                class="section-add-btn"
              >
                {{ 'PROPOSAL_REVIEW.ADD_HEADER' | translate }}
              </button>

              <button
                type="button"
                (click)="
                  addRow(block)
                "
                [disabled]="
                  getLeafColumns(
                    block
                  ).length === 0
                "
                class="section-add-btn"
              >
                {{ 'PROPOSAL_REVIEW.ADD_ROW' | translate }}
              </button>
            </div>
          </div>

          <div
            *ngIf="
              block.table.columns
                .length === 0
            "
            class="rounded-xl border border-dashed border-gray-300 p-7 text-center text-gray-500"
          >
            {{ 'PROPOSAL_REVIEW.ADD_HEADER_FIRST' | translate }}
          </div>

          <div
            *ngIf="
              block.table.columns
                .length > 0
            "
            class="overflow-x-auto rounded-xl border border-gray-200"
          >
            <table
              class="w-full min-w-[900px]"
            >
              <thead>
                <!-- Main headers -->
                <tr>
                  <ng-container
                    *ngFor="
                      let header of
                        block.table
                          .columns;
                      let headerIndex =
                        index;
                      trackBy:
                        trackByIndex
                    "
                  >
                    <th
                      [attr.colspan]="
                        getColumnLeafCount(
                          header
                        )
                      "
                      [attr.rowspan]="
                        getHeaderRowSpan(
                          block,
                          header
                        )
                      "
                      class="min-w-[200px] bg-[#1E2A78] p-3"
                    >
                      <div
                        class="flex items-center gap-2"
                      >
                        <input
                          [(ngModel)]="
                            header.title
                          "
                          [placeholder]="'PROPOSAL_REVIEW.HEADER_NAME' | translate"
                          class="h-10 w-full rounded-lg border border-transparent bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-indigo-300"
                        />

                        <button
                          type="button"
                          (click)="
                            addSubColumn(
                              block,
                              headerIndex
                            )
                          "
                          class="section-add-btn section-add-btn-sm"
                          [title]="'PROPOSAL_REVIEW.ADD_SUB_COLUMN' | translate"
                        >
                          {{ 'PROPOSAL_REVIEW.ADD_SUB' | translate }}
                        </button>

                        <button
                          type="button"
                          (click)="
                            removeHeader(
                              block,
                              headerIndex
                            )
                          "
                          class="section-delete-btn section-delete-btn-sm"
                          [title]="'PROPOSAL_REVIEW.DELETE_HEADER' | translate"
                        >
                          {{ 'COMMON.DELETE' | translate }}
                        </button>
                      </div>
                    </th>
                  </ng-container>

                  <th
                    [attr.rowspan]="
                      hasAnySubColumns(
                        block
                      )
                        ? 2
                        : 1
                    "
                    class="w-28 bg-[#1E2A78] text-sm text-white"
                  >
                    {{ 'COMMON.ACTION' | translate }}
                  </th>
                </tr>

                <!-- Sub headers -->
                <tr
                  *ngIf="
                    hasAnySubColumns(
                      block
                    )
                  "
                >
                  <ng-container
                    *ngFor="
                      let header of
                        block.table
                          .columns;
                      let headerIndex =
                        index;
                      trackBy:
                        trackByIndex
                    "
                  >
                    <th
                      *ngFor="
                        let subColumn of
                          header
                            .subColumns;
                        let subIndex =
                          index;
                        trackBy:
                          trackByIndex
                      "
                      class="min-w-[180px] bg-[#26358f] p-3"
                    >
                      <div
                        class="flex items-center gap-2"
                      >
                        <input
                          [(ngModel)]="
                            header
                              .subColumns[
                              subIndex
                            ]
                          "
                          [placeholder]="'PROPOSAL_REVIEW.SUB_HEADER' | translate"
                          class="h-10 w-full rounded-lg border border-transparent bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-indigo-300"
                        />

                        <button
                          type="button"
                          (click)="
                            removeSubColumn(
                              block,
                              headerIndex,
                              subIndex
                            )
                          "
                          class="section-delete-btn section-delete-btn-sm"
                          [title]="'PROPOSAL_REVIEW.DELETE_SUB_HEADER' | translate"
                        >
                          {{ 'COMMON.DELETE' | translate }}
                        </button>
                      </div>
                    </th>
                  </ng-container>
                </tr>
              </thead>

              <tbody>
                <tr
                  *ngFor="
                    let row of
                      block.table.rows;
                    let rowIndex =
                      index;
                    trackBy:
                      trackByIndex
                  "
                  class="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td
                    *ngFor="
                      let leaf of
                        getLeafColumns(
                          block
                        );
                      let colIndex =
                        index;
                      trackBy:
                        trackByIndex
                    "
                    class="p-3"
                  >
                    <input
                      [(ngModel)]="
                        block.table.rows[
                          rowIndex
                        ][colIndex]
                      "
                      [placeholder]="'PROPOSAL_REVIEW.ENTER_VALUE' | translate"
                      class="h-10 w-full rounded-lg border border-gray-300 px-3 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    />
                  </td>

                  <td
                    class="p-3 text-center"
                  >
                    <button
                      type="button"
                      (click)="
                        removeRow(
                          block,
                          rowIndex
                        )
                      "
                      class="section-delete-btn"
                    >
                      {{ 'COMMON.DELETE' | translate }}
                    </button>
                  </td>
                </tr>

                <tr
                  *ngIf="
                    block.table.rows
                      .length === 0
                  "
                >
                  <td
                    [attr.colspan]="
                      getLeafColumns(
                        block
                      ).length + 1
                    "
                    class="p-8 text-center text-gray-500"
                  >
                    {{ 'PROPOSAL_REVIEW.NO_ROWS' | translate }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>

  <app-popup
    [(show)]="showDeletePopup"
    title="PROPOSAL_REVIEW.DELETE_BLOCK"
    type="confirm"
    [message]="deleteMessage"
    confirmText="COMMON.DELETE"
    cancelText="COMMON.CANCEL"
    (confirmed)="confirmRemoveBlock()"
  ></app-popup>
</div>

<!-- ====================================================== -->
<!-- PREVIEW MODE -->
<!-- ====================================================== -->

<ng-template #previewMode>
  <div
    *ngIf="loading"
    class="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-500"
  >
    {{ 'PROPOSAL_REVIEW.LOADING' | translate }}
  </div>

  <div
    *ngIf="
      !loading &&
      activeBlocks.length === 0
    "
    class="rounded-2xl border border-gray-200 bg-gray-50 p-10 text-center text-gray-500"
  >
    {{ 'PROPOSAL_REVIEW.NO_CONTENT_PREVIEW' | translate }}
  </div>

  <div
    *ngFor="
      let block of activeBlocks;
      trackBy: trackByBlock
    "
    class="mb-8"
  >
    <!-- Heading -->
    <h2
      *ngIf="
        block.type ===
        'heading'
      "
      class="border-b border-gray-200 pb-3 text-2xl font-semibold text-[#1E2A78]"
    >
      {{ block.title }}
    </h2>

    <!-- Paragraph -->
    <div
      *ngIf="
        block.type ===
        'paragraph'
      "
      class="text-gray-700"
    >
      <h3
        *ngIf="block.title"
        class="mb-3 text-lg font-semibold text-[#1E2A78]"
      >
        {{ block.title }}
      </h3>

      <div
        class="rich-text-content"
        [innerHTML]="block.content | richText"
      ></div>
    </div>

    <!-- Bullet list -->
    <div
      *ngIf="
        block.type ===
        'bullet-list'
      "
    >
      <h3
        *ngIf="block.title"
        class="mb-3 text-lg font-semibold text-[#1E2A78]"
      >
        {{ block.title }}
      </h3>

      <ul
        class="list-disc space-y-2 ps-8 text-gray-700"
      >
        <li
          *ngFor="
            let line of
              getLines(
                block.content
              )
          "
        >
          {{ line }}
        </li>
      </ul>
    </div>

    <!-- Numbered list -->
    <div
      *ngIf="
        block.type ===
        'numbered-list'
      "
    >
      <h3
        *ngIf="block.title"
        class="mb-3 text-lg font-semibold text-[#1E2A78]"
      >
        {{ block.title }}
      </h3>

      <ol
        class="list-decimal space-y-2 ps-8 text-gray-700"
      >
        <li
          *ngFor="
            let line of
              getLines(
                block.content
              )
          "
        >
          {{ line }}
        </li>
      </ol>
    </div>

    <!-- Table -->
    <div
      *ngIf="
        block.type ===
          'table' &&
        block.table &&
        block.table.columns
          .length
      "
      class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
    >
      <div
        *ngIf="block.title"
        class="border-b border-gray-200 px-6 py-4"
      >
        <h3
          class="text-xl font-semibold text-[#1E2A78]"
        >
          {{ block.title }}
        </h3>
      </div>

      <div class="overflow-x-auto">
        <table
          class="w-full min-w-[700px]"
        >
          <thead>
            <tr
              class="bg-[#1E2A78] text-white"
            >
              <ng-container
                *ngFor="
                  let header of
                    block.table
                      .columns
                "
              >
                <th
                  [attr.colspan]="
                    getColumnLeafCount(
                      header
                    )
                  "
                  [attr.rowspan]="
                    getHeaderRowSpan(
                      block,
                      header
                    )
                  "
                  class="border border-indigo-400 px-5 py-4 text-center text-sm font-semibold"
                >
                  {{
                    header.title ||
                    ('PROPOSAL_REVIEW.UNTITLED_HEADER' | translate)
                  }}
                </th>
              </ng-container>
            </tr>

            <tr
              *ngIf="
                hasAnySubColumns(
                  block
                )
              "
              class="bg-[#26358f] text-white"
            >
              <ng-container
                *ngFor="
                  let header of
                    block.table
                      .columns
                "
              >
                <th
                  *ngFor="
                    let subColumn of
                      header
                        .subColumns
                  "
                  class="border border-indigo-400 px-5 py-3 text-center text-sm font-semibold"
                >
                  {{
                    subColumn ||
                    ('PROPOSAL_REVIEW.UNTITLED_SUB_HEADER' | translate)
                  }}
                </th>
              </ng-container>
            </tr>
          </thead>

          <tbody>
            <tr
              *ngFor="
                let row of
                  block.table.rows;
                let rowIndex =
                  index
              "
              class="border-b border-gray-100 last:border-b-0"
              [class.bg-gray-50]="
                rowIndex % 2 === 1
              "
            >
              <td
                *ngFor="
                  let cell of row;
                  let cellIndex = index
                "
                class="border border-gray-200 px-5 py-3 text-sm text-gray-700"
                [class.text-end]="(cell | tableCell: block.table.columns : cellIndex).numeric"
                [class.tabular-nums]="(cell | tableCell: block.table.columns : cellIndex).numeric"
              >
                {{ (cell | tableCell: block.table.columns : cellIndex).text || '-' }}
              </td>
            </tr>

            <tr
              *ngIf="
                block.table.rows
                  .length === 0
              "
            >
              <td
                [attr.colspan]="
                  getLeafColumns(
                    block
                  ).length
                "
                class="px-5 py-8 text-center text-gray-500"
              >
                {{ 'PROPOSAL_REVIEW.NO_DATA' | translate }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        *ngIf="block.content"
        class="whitespace-pre-line border-t border-gray-100 px-5 py-4 text-sm leading-6 text-gray-600"
      >
        {{ block.content }}
      </div>
    </div>
  </div>
</ng-template>